import type { AreaProducerTier, AreaReport, AreaVintage } from "@corklog/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { producerKey, type HeldMark } from "../area-match.js";
import { countryName } from "../countries.js";
import { locale } from "../locale.js";
import { AiNotice } from "./AiNotice.js";
import { AreaMap } from "./AreaMap.js";
import { Flag } from "./Flag.js";

/**
 * One area report, drawn — the page both the administrator reads a draft on
 * and a reader reads what is published on, so that what is published is what
 * was read. Decided 2026-09-28 in `project-scope.md`; the layout is the
 * Champagne preview's, which the owner saw before anything was built.
 *
 * **In the order a reader needs it**: the area, how the wine is made, the
 * regions with their producers, the years, and last what the model was unsure
 * of — last because it qualifies everything above it and is read once the
 * rest is known.
 *
 * `held` marks what the reader's own cellars already hold, by producer name —
 * worked out by the page from the cellars' own records and never by the model.
 * See `Discover.tsx`.
 */
export function AreaReportView({
  report,
  held,
}: {
  report: AreaReport;
  held?: ReadonlyMap<string, HeldMark>;
}) {
  const { t } = useTranslation();
  const { content } = report;
  // A map needs one position to stand on; without it the regions take the
  // whole width rather than leaving a column empty.
  const hasMap = content.regions.some((region) => region.position !== null);

  return (
    <article className="space-y-12">
      <header className="space-y-2">
        <h1 className="font-display text-3xl text-balance">{report.area}</h1>
        <p className="text-muted flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span>{t(`wineType.${report.kind}`)}</span>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1.5">
            <Flag countryCode={report.countryCode} />
            {countryName(report.countryCode)}
          </span>
        </p>
        <p className="text-muted text-xs">
          {t("area.byline", {
            model: report.model ?? t("area.theModel"),
            date: formatDate(report.publishedAt ?? report.runAt),
          })}
        </p>
        <AiNotice className="pt-1" />
      </header>

      <section className="space-y-4">
        <h2 className="font-display text-xl">{t("area.overview")}</h2>
        {paragraphs(content.summary).map((paragraph, index) => (
          <p key={index} className="max-w-prose">
            {paragraph}
          </p>
        ))}
        <dl className="border-line grid max-w-prose gap-px overflow-hidden rounded-sm border bg-line sm:grid-cols-2">
          <div className="bg-surface px-4 py-3">
            <dt className="text-muted text-sm">{t("area.keyGrapes")}</dt>
            <dd>{content.keyGrapes.join(", ")}</dd>
          </div>
          {content.classification !== null && (
            <div className="bg-surface px-4 py-3">
              <dt className="text-muted text-sm">{t("area.classification")}</dt>
              <dd>{content.classification}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="font-display text-xl">{t("area.production")}</h2>
        <p className="max-w-prose">{content.production.summary}</p>
        {/* The whole process at a glance, and the detail a click away — asked
            for on 2026-09-29, when the steps and choices took two screens.
            The names in one numbered line, since the order is the information;
            the descriptions and the choices folded, `<details>` so folding is
            the browser's own, keyboard and screen reader included. */}
        <p className="flex max-w-prose flex-wrap gap-x-3 gap-y-1 text-sm">
          {content.production.steps.map((step, index) => (
            <span key={index} className="whitespace-nowrap">
              <span className="font-display text-accent tabular-nums">{index + 1}</span>{" "}
              {step.name}
            </span>
          ))}
        </p>
        <details className="max-w-prose">
          <summary className="text-accent cursor-pointer text-sm underline-offset-4 hover:underline">
            {t("area.showSteps")}
          </summary>
          <ol className="mt-2">
            {content.production.steps.map((step, index) => (
              <li
                key={index}
                className="border-line grid grid-cols-[2rem_1fr] gap-x-3 border-t py-3 first:border-t-0"
              >
                <span className="font-display text-accent text-lg tabular-nums">
                  {index + 1}
                </span>
                <div>
                  <p className="font-display font-semibold">{step.name}</p>
                  <p className="text-muted mt-0.5">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </details>
        {content.production.choices.length > 0 && (
          <details>
            <summary className="text-accent cursor-pointer text-sm underline-offset-4 hover:underline">
              {t("area.showChoices", { count: content.production.choices.length })}
            </summary>
            <ul className="mt-3 grid gap-3 sm:grid-cols-2">
              {content.production.choices.map((choice, index) => (
                <li
                  key={index}
                  className="bg-surface border-line rounded-sm border px-4 py-3"
                >
                  <p className="font-display font-semibold">{choice.name}</p>
                  <p className="text-muted mt-1 text-sm">{choice.description}</p>
                  {choice.examples.length > 0 && (
                    <p className="mt-2 text-sm">
                      <span className="text-muted">{t("area.examples")} </span>
                      {choice.examples.join(", ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      <section className="space-y-6">
        <h2 className="font-display text-xl">{t("area.regions")}</h2>
        {/* The map stands still on the left while the regions scroll past on
            the right — the preview's layout, which the owner asked to keep.
            On a phone it goes above the list: two columns do not fit, and a
            map pinned over half a small screen would hide what it explains.
            On a wide screen the section reaches past the page's measure, as
            the preview's page was wider, so the region texts keep a readable
            line beside the map; the rest of the report keeps the measure. */}
        <div
          className={
            hasMap
              ? "grid gap-8 md:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] md:items-start lg:-mx-28 lg:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]"
              : ""
          }
        >
          {hasMap && (
            <div className="md:sticky md:top-6">
              <AreaMap regions={content.regions} countryCode={report.countryCode} />
            </div>
          )}
          <div className="space-y-8">
            {content.regions.map((region, index) => (
              <div key={index} className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-3">
                  <h3 className="font-display text-lg">{region.name}</h3>
                  <span className="text-muted text-sm">{region.level}</span>
                </div>
                <p className="max-w-prose">{region.description}</p>
                {region.grapes.length > 0 && (
                  <p className="text-muted text-sm">{region.grapes.join(", ")}</p>
                )}
                {region.producers.length > 0 && (
                  <ul className="border-line max-w-prose divide-y divide-line rounded-sm border">
                    {region.producers.map((producer, at) => {
                      const mark = held?.get(producerKey(producer.name));
                      return (
                        <li key={at} className="bg-surface px-4 py-3">
                          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                            <span className="font-display font-semibold">
                              {producer.name}
                            </span>
                            <Tier tier={producer.tier} />
                          </div>
                          <p className="text-muted mt-1 text-sm">{producer.why}</p>
                          {producer.cuvees.length > 0 && (
                            <p className="mt-1 text-sm">
                              <span className="text-muted">{t("area.cuvees")} </span>
                              {producer.cuvees.join(", ")}
                            </p>
                          )}
                          {mark !== undefined && (
                            // One link per name the cellar holds it under, so
                            // each count is what its search will find.
                            <p className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-sm">
                              {mark.map((match) => (
                                <Link
                                  key={match.to}
                                  to={match.to}
                                  className="text-ready underline-offset-4 hover:underline"
                                >
                                  {match.name === producer.name
                                    ? t("area.held", {
                                        count: match.wines,
                                        cellar: match.cellar,
                                      })
                                    : t("area.heldAs", {
                                        count: match.wines,
                                        cellar: match.cellar,
                                        name: match.name,
                                      })}
                                </Link>
                              ))}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {content.vintages.length > 0 && <Vintages vintages={content.vintages} />}

      {content.caveats.length > 0 && (
        <section className="bg-raised space-y-2 rounded-sm px-5 py-4">
          <h2 className="font-display text-lg">{t("area.caveats")}</h2>
          <ul className="max-w-prose list-disc space-y-1 pl-5 text-sm">
            {content.caveats.map((caveat, index) => (
              <li key={index}>{caveat}</li>
            ))}
          </ul>
        </section>
      )}

      <p className="text-muted max-w-prose text-xs">
        {t("area.source", { model: report.model ?? t("area.theModel") })}
      </p>
    </article>
  );
}

function Tier({ tier }: { tier: AreaProducerTier }) {
  const { t } = useTranslation();
  const tone =
    tier === "reference"
      ? "border-accent text-accent"
      : tier === "discovery"
        ? "border-line text-muted border-dashed"
        : "border-line text-muted";
  return (
    <span className={`rounded-full border px-2 py-px text-xs whitespace-nowrap ${tone}`}>
      {t(`area.tier.${tier}`)}
    </span>
  );
}

/**
 * The vintage table, Systembolaget's shape.
 *
 * **The grade is five marks and a number**, never colour alone: an empty year
 * is five empty marks and a dash, which is a different shape from a low one,
 * because it is a different fact.
 */
function Vintages({ vintages }: { vintages: readonly AreaVintage[] }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl">{t("area.vintages")}</h2>
      <p className="text-muted max-w-prose text-sm">{t("area.vintagesIntro")}</p>
      <div className="overflow-x-auto">
        <table className="bg-surface border-line w-full min-w-[30rem] border text-sm">
          <thead>
            <tr className="text-muted text-left">
              <th scope="col" className="px-3 py-2 font-normal">
                {t("area.year")}
              </th>
              <th scope="col" className="px-3 py-2 font-normal">
                {t("area.grade")}
              </th>
              <th scope="col" className="px-3 py-2 font-normal">
                {t("area.remark")}
              </th>
            </tr>
          </thead>
          <tbody>
            {vintages.map((line) => (
              <tr key={line.year} className="border-line border-t align-top">
                <td className="font-display w-16 px-3 py-2 font-semibold tabular-nums">
                  {line.year}
                </td>
                <td className="w-32 px-3 py-2 whitespace-nowrap">
                  <span
                    className="inline-flex items-center gap-2"
                    aria-label={
                      line.grade === null
                        ? t("area.noGrade")
                        : t("area.gradeOf", { grade: line.grade })
                    }
                  >
                    <span className="inline-flex gap-0.5" aria-hidden="true">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span
                          key={n}
                          className={`h-2.5 w-2.5 rounded-[2px] ${
                            line.grade !== null && n <= line.grade
                              ? "bg-accent"
                              : "bg-line"
                          }`}
                        />
                      ))}
                    </span>
                    <span
                      aria-hidden="true"
                      className={`font-display tabular-nums ${line.grade === null ? "text-muted" : "font-semibold"}`}
                    >
                      {line.grade ?? "–"}
                    </span>
                  </span>
                </td>
                <td className="text-muted px-3 py-2">{line.remark}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function paragraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== "");
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(locale(), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}
